import { syncOrthoFrustum, snapToVertex } from "./viewerUtils.js";

export function createControlsManager({
  THREE, container, controls, meshObjects, allMaterials, scene,
  getCameraFn, pushCamStateFn, orthoCamera, file, onSelect, measureManager, getModelScaleFn, getOffsetFn, onTrack, onSectionClick,
}) {

  let currentMode   = "rotate";
  let mouseDownPos  = { x: 0, y: 0 };
  let mouseDownTime = 0;
  let highlightedMesh = null;

  function highlightMesh(mesh) {
    if (highlightedMesh && highlightedMesh !== mesh) unhighlightMesh(highlightedMesh);
    if (!mesh) { highlightedMesh = null; return; }
    if (!mesh.userData.materialCloned) {
      mesh.material = mesh.material.clone();
      mesh.userData.materialCloned = true;
    }
    mesh.material.emissive          = new THREE.Color(0x0077ff);
    mesh.material.emissiveIntensity = 0.45;
    highlightedMesh = mesh;
  }

  function unhighlightMesh(mesh) {
    if (!mesh) return;
    mesh.material.emissive          = new THREE.Color(0x000000);
    mesh.material.emissiveIntensity = 0;
  }

  function clearHighlight() { unhighlightMesh(highlightedMesh); highlightedMesh = null; }

  // ── Rotate around cursor ───────────────────────────────────────────────────
  let pendingOrbitTarget = null;
  let orbitDragCommitted = false;
  const orbitRC    = new THREE.Raycaster();
  const orbitMouse = new THREE.Vector2();

  function onMouseDownOrbit(e) {
    if (currentMode !== "rotate" || e.button !== 0) return;
    pendingOrbitTarget = null; orbitDragCommitted = false;
    const rect = container.getBoundingClientRect();
    orbitMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    orbitMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    orbitRC.setFromCamera(orbitMouse, getCameraFn());
    const hits = orbitRC.intersectObjects(meshObjects, false);
    if (hits.length > 0) {
      pendingOrbitTarget = hits[0].point.clone();
    } else {
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
        getCameraFn().getWorldDirection(new THREE.Vector3()), controls.target
      );
      const pt = new THREE.Vector3();
      if (orbitRC.ray.intersectPlane(plane, pt)) pendingOrbitTarget = pt.clone();
    }
  }

  function onMouseMoveOrbit(e) {
    if (e.buttons !== 1 || orbitDragCommitted || currentMode !== "rotate") return;
    if (pendingOrbitTarget) {
      // Suppress orbit-around-cursor when a tentative snap is active — keep the snap point
      if (!tentativeMarker) {
        controls.target.copy(pendingOrbitTarget);
        controls.update();
      }
      pendingOrbitTarget = null;
    }
    orbitDragCommitted = true;
  }

  // ── Tentative snap (right-click) ──────────────────────────────────────────
  let tentativeMarker  = null;
  let tentativeTexture = null;
  let rightDownPos     = { x: 0, y: 0 };
  let rightDownTime    = 0;
  let lastRightClick   = 0;
  const tentRC    = new THREE.Raycaster();
  const tentMouse = new THREE.Vector2();

  function getTentativeTexture() {
    if (tentativeTexture) return tentativeTexture;
    const canvas = document.createElement("canvas");
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    const c = 32, arm = 18;
    ctx.beginPath(); ctx.moveTo(c - arm, c); ctx.lineTo(c + arm, c); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(c, c - arm); ctx.lineTo(c, c + arm); ctx.stroke();
    tentativeTexture = new THREE.CanvasTexture(canvas);
    return tentativeTexture;
  }

  function clearTentativeMarker() {
    if (!tentativeMarker) return;
    scene.remove(tentativeMarker);
    tentativeMarker.material.dispose();
    tentativeMarker = null;
  }

  function resetOrbitCenter() {
    if (meshObjects.length === 0) return;
    const box = new THREE.Box3();
    meshObjects.forEach((m) => box.expandByObject(m));
    controls.target.copy(box.getCenter(new THREE.Vector3()));
    controls.update();
  }

  function activateTentativeSnap(e) {
    const rect = container.getBoundingClientRect();
    tentMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    tentMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    tentRC.setFromCamera(tentMouse, getCameraFn());
    const hits = tentRC.intersectObjects(meshObjects, false);
    if (hits.length === 0) return;

    const modelScale = getModelScaleFn ? getModelScaleFn() : 1;
    const snap = snapToVertex(hits[0], modelScale * 0.02);
    controls.target.copy(snap);
    controls.update();

    clearTentativeMarker();
    // Use sizeAttenuation:true with model-proportional scale — works in both persp and ortho
    const s = Math.max(modelScale, 1) * 0.0003;
    const mat = new THREE.SpriteMaterial({
      map: getTentativeTexture(),
      depthTest: false,
      sizeAttenuation: true,
    });
    tentativeMarker = new THREE.Sprite(mat);
    tentativeMarker.scale.set(s, s, 1);
    tentativeMarker.position.copy(snap);
    tentativeMarker.renderOrder = 1001;
    scene.add(tentativeMarker);
  }

  // ── XYZ Tracker ───────────────────────────────────────────────────────────
  const trackerRC    = new THREE.Raycaster();
  const trackerMouse = new THREE.Vector2();

  function onMouseMoveTracker(e) {
    const rect = container.getBoundingClientRect();
    trackerMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    trackerMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    trackerRC.setFromCamera(trackerMouse, getCameraFn());
    const hits = trackerRC.intersectObjects(meshObjects, false);
    if (hits.length > 0) {
      const modelScale = getModelScaleFn ? getModelScaleFn() : 1;
      const snap   = snapToVertex(hits[0], modelScale * 0.02);
      // Add back the recentering offset to restore project (model) coordinates
      const offset = getOffsetFn ? getOffsetFn() : { x: 0, y: 0, z: 0 };
      if (onTrack) onTrack({ x: snap.x + offset.x, y: snap.y + offset.y, z: snap.z + offset.z });
    } else {
      if (onTrack) onTrack(null);
    }
  }

  // ── Window zoom ────────────────────────────────────────────────────────────
  let wzDragging = false;
  let wzStart    = { x: 0, y: 0 };

  const wzOverlay = document.createElement("div");
  wzOverlay.style.cssText = "position:absolute;border:2px solid #63b3ed;background:rgba(99,179,237,0.12);pointer-events:none;display:none;box-sizing:border-box;";
  container.style.position = "relative";
  container.appendChild(wzOverlay);

  function onMouseDownWZoom(e) {
    if (currentMode !== "wzoom" || e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    wzDragging = true;
    const rect = container.getBoundingClientRect();
    wzStart = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    Object.assign(wzOverlay.style, { left: wzStart.x+"px", top: wzStart.y+"px", width:"0px", height:"0px", display:"block" });
  }

  function onMouseMoveWZoom(e) {
    if (!wzDragging) return;
    const rect = container.getBoundingClientRect();
    const cur  = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    Object.assign(wzOverlay.style, {
      left: Math.min(wzStart.x, cur.x)+"px", top: Math.min(wzStart.y, cur.y)+"px",
      width: Math.abs(cur.x - wzStart.x)+"px", height: Math.abs(cur.y - wzStart.y)+"px",
    });
  }

  function onMouseUpWZoom(e) {
    if (!wzDragging) return;
    wzDragging = false; wzOverlay.style.display = "none";
    const rect = container.getBoundingClientRect();
    const cur  = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const boxW = Math.abs(cur.x - wzStart.x);
    const boxH = Math.abs(cur.y - wzStart.y);
    if (boxW < 10 || boxH < 10) return;

    const cam     = getCameraFn();
    const viewDir = cam.getWorldDirection(new THREE.Vector3());
    const plane   = new THREE.Plane().setFromNormalAndCoplanarPoint(viewDir, controls.target);
    const toWorld = ({ x, y }) => {
      const ndc = new THREE.Vector2((x / rect.width) * 2 - 1, -(y / rect.height) * 2 + 1);
      const ray = new THREE.Raycaster(); ray.setFromCamera(ndc, cam);
      const pt = new THREE.Vector3();
      return ray.ray.intersectPlane(plane, pt) ? pt : null;
    };
    const tl = toWorld(wzStart), br = toWorld(cur);
    if (!tl || !br) return;

    pushCamStateFn();
    const newTarget = new THREE.Vector3().addVectors(tl, br).multiplyScalar(0.5);
    const zoomRatio = Math.min(rect.width / boxW, rect.height / boxH);
    const newDist   = Math.max(cam.position.distanceTo(controls.target) / zoomRatio, controls.minDistance * 2);
    cam.position.copy(newTarget.clone().addScaledVector(viewDir, -newDist));
    controls.target.copy(newTarget);
    controls.update();
    syncOrthoFrustum(orthoCamera, controls, container.clientWidth, container.clientHeight);
  }

  // ── Cross-section picking ─────────────────────────────────────────────────
  let sectionPickStep = 0; // 0 = awaiting p1, 1 = awaiting p2
  let sectionPoint1   = null;

  // ── Selection ──────────────────────────────────────────────────────────────
  const clickRC    = new THREE.Raycaster();
  const clickMouse = new THREE.Vector2();

  async function onClickSectionPick(e) {
    const rect = container.getBoundingClientRect();
    clickMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    clickMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    clickRC.setFromCamera(clickMouse, getCameraFn());
    const hits = clickRC.intersectObjects(meshObjects, false);

    let point;
    if (hits.length > 0) {
      const modelScale = getModelScaleFn ? getModelScaleFn() : 1;
      point = snapToVertex(hits[0], modelScale * 0.02);
    } else {
      // No mesh hit — project ray onto horizontal plane at current orbit target Z
      // so the user can draw the section line freely on screen.
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -controls.target.z);
      const hit   = new THREE.Vector3();
      if (!clickRC.ray.intersectPlane(plane, hit)) return;
      point = hit;
    }

    if (sectionPickStep === 0) {
      sectionPoint1    = point.clone();
      sectionPickStep  = 1;
      if (onSectionClick) onSectionClick("first", point);
    } else {
      const p2        = point.clone();
      sectionPickStep = 0;
      if (onSectionClick) onSectionClick("second", sectionPoint1, p2);
      sectionPoint1   = null;
    }
  }

  async function onClickSelect(e) {
    const rect = container.getBoundingClientRect();
    clickMouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    clickMouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    clickRC.setFromCamera(clickMouse, getCameraFn());
    const hits = clickRC.intersectObjects(meshObjects, false);

    if (hits.length > 0) {
      const { expressID } = hits[0].object.userData;
      highlightMesh(hits[0].object);
      if (expressID) {
        onSelect({ _name: `Element ${expressID}`, _elementId: expressID }, true);
        try {
          const r    = await fetch(`/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/properties/${expressID}`);
          const data = await r.json();

          const el       = {};
          el._name       = data.userLabel || data.codeValue || `Element ${expressID}`;
          el._class      = data.className  || "";
          el._elementId  = data.elementId;
          el._aspects    = data.aspects    || [];
          if (data.codeValue)   el.Code        = data.codeValue;
          if (data.description) el.Description = data.description;

          onSelect(el, false);
        } catch {
          onSelect({ _name: `Element ${expressID}`, _elementId: expressID }, false);
        }
      }
    } else {
      clearHighlight();
      onSelect(null, false);
    }
  }

  function handleMouseDown(e) {
    mouseDownPos  = { x: e.clientX, y: e.clientY };
    mouseDownTime = Date.now();
    if (e.button === 2) { rightDownPos = { x: e.clientX, y: e.clientY }; rightDownTime = Date.now(); }
    onMouseDownOrbit(e);
    onMouseDownWZoom(e);
  }

  function handleMouseMove(e) {
    onMouseMoveOrbit(e);
    onMouseMoveWZoom(e);
    if (currentMode === "measure" || currentMode === "tracker") {
      measureManager.onMouseMove(e, container, getCameraFn());
    } else {
      measureManager.hideSnapMarker();
    }
    if (currentMode === "tracker") onMouseMoveTracker(e);
  }

  function handleMouseUp(e) {
    onMouseUpWZoom(e);
    if (e.button === 2) {
      const dx = Math.abs(e.clientX - rightDownPos.x);
      const dy = Math.abs(e.clientY - rightDownPos.y);
      const now = Date.now();
      if (dx <= 5 && dy <= 5 && now - rightDownTime <= 400) {
        if (now - lastRightClick < 350) {
          // Double right-click: clear "+" and reset orbit to model center
          clearTentativeMarker();
          resetOrbitCenter();
          lastRightClick = 0;
        } else {
          // Single right-click: snap to nearest mesh point
          activateTentativeSnap(e);
          lastRightClick = now;
        }
      }
    }
  }

  function handleContextMenu(e) { e.preventDefault(); }

  async function handleClick(e) {
    const dx = Math.abs(e.clientX - mouseDownPos.x);
    const dy = Math.abs(e.clientY - mouseDownPos.y);
    if (dx > 5 || dy > 5 || Date.now() - mouseDownTime > 300) return;
    if (currentMode === "select")  await onClickSelect(e);
    if (currentMode === "measure") measureManager.onClick(e, container, getCameraFn());
    if (currentMode === "section") await onClickSectionPick(e);
  }

  container.addEventListener("mousedown",   handleMouseDown);
  container.addEventListener("mousedown",   handleMouseDown, true);
  container.addEventListener("mousemove",   handleMouseMove);
  container.addEventListener("mouseup",     handleMouseUp);
  container.addEventListener("click",       handleClick);
  container.addEventListener("contextmenu", handleContextMenu);

  return {
    setMode(mode) {
      clearTentativeMarker(); // clear "+" whenever a toolbar mode is activated
      currentMode = mode;
      if (mode !== "measure" && mode !== "tracker") measureManager.clear();
      if (mode !== "select")  { clearHighlight(); onSelect(null, false); }
      if (mode !== "tracker" && onTrack) onTrack(null); // clear tracker popup

      if (mode === "pan") {
        controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
        controls.enabled = true;
      } else if (mode === "select" || mode === "measure") {
        controls.mouseButtons = { LEFT: -1, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        controls.enabled = true;
      } else if (mode === "wzoom") {
        controls.mouseButtons = { LEFT: -1, MIDDLE: -1, RIGHT: -1 };
        controls.enabled = false;
      } else if (mode === "section") {
        controls.mouseButtons = { LEFT: -1, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        controls.enabled = true;
        sectionPickStep = 0;
        sectionPoint1   = null;
      } else {
        // rotate (default) and tracker — allow free orbit while tracking
        controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        controls.enabled = true;
      }
    },

    setWireframe(enabled) { allMaterials.forEach((m) => { m.wireframe = enabled; }); },

    dispose() {
      container.removeEventListener("mousedown",   handleMouseDown);
      container.removeEventListener("mousedown",   handleMouseDown, true);
      container.removeEventListener("mousemove",   handleMouseMove);
      container.removeEventListener("mouseup",     handleMouseUp);
      container.removeEventListener("click",       handleClick);
      container.removeEventListener("contextmenu", handleContextMenu);
      clearTentativeMarker();
      if (tentativeTexture) { tentativeTexture.dispose(); tentativeTexture = null; }
      if (wzOverlay.parentNode) wzOverlay.parentNode.removeChild(wzOverlay);
    },
  };
}
